import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link, useHistory } from "react-router-dom";
import "../loader.css";

function AppUserList() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useHistory();

  const getToken = () => {
    try {
      const raw = localStorage.getItem("state");
      if (raw) return JSON.parse(raw).auth?.token;
    } catch (e) { }
    return null;
  };

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = () => {
    const token = getToken();
    axios.get(`${process.env.REACT_APP_API_URL}/AppUser/GetAllAppUsers`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) }
    }).then(res => {
      setUsers(res.data || []);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  };

  const toggleStatus = (userId, currentStatus) => {
    const token = getToken();
    Swal.fire({
      title: currentStatus ? "Deactivate user?" : "Activate user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes"
    }).then(result => {
      if (!result.isConfirmed) return;
      axios.post(`${process.env.REACT_APP_API_URL}/AppUser/SetUserStatus`,
        { UserId: userId, Active: !currentStatus },
        { headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) } }
      ).then(() => {
        Swal.fire({ title: "Done", icon: "success", confirmButtonText: "Ok" })
          .then(() => loadUsers());
      }).catch(err => {
        Swal.fire({ title: "Error", text: err.response?.data || "Failed", icon: "error", confirmButtonText: "Ok" });
      });
    });
  };

  const loginAsUser = (user) => {
    const token = getToken();
    const userId = user.id || user.Id;
    const email = user.email || user.Email;

    Swal.fire({
      title: `Login as ${email}?`,
      text: "You will be switched to their account. Log out to return to admin.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, switch"
    }).then(result => {
      if (!result.isConfirmed) return;

      axios.post(`${process.env.REACT_APP_API_URL}/AppUser/AdminLoginAsUser`,
        { UserId: userId },
        { headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) } }
      ).then(res => {
        // Store admin session so we can restore it on logout
        const currentState = localStorage.getItem("state");
        localStorage.setItem("adminSnapshot", currentState);
        localStorage.setItem("adminRole", localStorage.getItem("URole") || "Admin");

        // Switch to AppUser session
        const newState = JSON.stringify({ auth: { token: res.data.token || res.data.Token, loggedIn: true } });
        localStorage.setItem("state", newState);
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("URole", "AppUser");
        localStorage.setItem("AppUserId", res.data.userId || res.data.UserId);

        window.location.href = "/";
      }).catch(err => {
        Swal.fire({ title: "Error", text: err.response?.data || "Failed", icon: "error", confirmButtonText: "Ok" });
      });
    });
  };

  const editUser = (user) => {
    localStorage.setItem("AppUserEditId", user.id || user.Id);
    localStorage.setItem("AppUserEditEmail", user.email || user.Email);
  };

  return (
    <div className="page-content-wrapper">
      {isLoading ? (
        <div className="page-content d-flex justify-content-center align-items-center">
          <div className="my-wrapper">
            <div className="spinner center">
              {[...Array(12)].map((_, i) => <div key={i} className="spinner-blade" />)}
            </div>
          </div>
        </div>
      ) : (
        <div className="page-content">
          <div className="page-bar">
            <div className="page-title-breadcrumb">
              <div className="pull-left">
                <div className="page-title">App User List</div>
              </div>
              <div className="pull-right">
                <Link to="/AddAppUser" className="btn" style={{ backgroundColor: "#1e2b38", color: "#FFF" }}>
                  <i className="fa fa-plus" /> Add User
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-head"><header>User List</header></div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped custom-table table-hover">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Cases</th>
                          <th>Created</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => {
                          const id = user.id || user.Id;
                          const email = user.email || user.Email;
                          const active = user.active_Status ?? user.Active_Status;
                          const created = user.created_Date || user.Created_Date;
                          const cases = user.cases || user.Cases || [];
                          return (
                            <tr key={id}>
                              <td>{email}</td>
                              <td>
                                {cases.length === 0
                                  ? <span style={{ color: "#999" }}>None</span>
                                  : cases
                                    .filter(c => (c.active_Status ?? c.Active_Status) === true)
                                    .map(c => {
                                      const pid = c.patient_Id ?? c.Patient_Id;
                                      const max = c.max_Characters ?? c.Max_Characters ?? 0;
                                      const used = c.used_Characters ?? c.Used_Characters ?? 0;
                                      return (
                                        <span key={pid}
                                          style={{ backgroundColor: "#1e2b38", color: "#fff", padding: "2px 8px", borderRadius: "4px", marginRight: "4px", fontSize: "12px" }}>
                                          Case {pid} ({max - used}/{max})
                                        </span>
                                      );
                                    })
                                }
                              </td>
                              <td>{created ? new Date(created).toLocaleDateString() : "-"}</td>
                              <td>
                                <span style={{
                                  backgroundColor: active ? "#28a745" : "#dc3545",
                                  color: "#fff", padding: "3px 10px", borderRadius: "4px", fontSize: "12px"
                                }}>
                                  {active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <Link to="/EditAppUser" onClick={() => editUser(user)}
                                  className="btn btn-sm btn-info" style={{ marginRight: "6px" }}>
                                  <i className="fa fa-edit" /> Edit
                                </Link>
                                {(user.active_Status ?? user.Active_Status) && (
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    style={{ marginRight: "6px" }}
                                    onClick={() => loginAsUser(user)}>
                                    <i className="fa fa-sign-in" /> Login As
                                  </button>
                                )}
                                <button
                                  className={`btn btn-sm ${active ? "btn-warning" : "btn-success"}`}
                                  onClick={() => toggleStatus(id, active)}>
                                  {active ? "Deactivate" : "Activate"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppUserList;